import { useMemo, useRef, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Line } from "@react-three/drei";

const CYAN = "#b88457";
const RED = "#c0453a";

// shared normalized hero-scroll progress (0 at top → 1 after one viewport)
function useHeroScroll() {
  const ref = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      ref.current = Math.min(window.scrollY / (window.innerHeight || 1), 1);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return ref;
}

function Vessel({ scrollRef }) {
  const group = useRef();
  const spin = useRef(0);
  const shards = useRef();

  // Jagged rupture seam running along the top longitudinal weld
  const { center, lipA, lipB } = useMemo(() => {
    const n = 30;
    const center = [];
    const lipA = [];
    const lipB = [];
    for (let i = 0; i <= n; i++) {
      const x = -1.25 + 2.5 * (i / n);
      const zig = (i % 2 === 0 ? 1 : -1) * 0.05 + (Math.random() - 0.5) * 0.03;
      const gap = 0.16 * Math.sin((i / n) * Math.PI); // opening widest at middle
      const mk = (theta) => [x, Math.cos(theta) * 1.015, Math.sin(theta) * 1.015];
      center.push(mk(zig));
      lipA.push(mk(zig + gap + 0.05));
      lipB.push(mk(zig - gap - 0.05));
    }
    return { center, lipA, lipB };
  }, []);

  const shardCfg = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, i) => ({
        pos: [-0.6 + i * 0.3 + (Math.random() - 0.5) * 0.2, 1.05 + Math.random() * 0.18, (Math.random() - 0.5) * 0.35],
        rot: [Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI],
        s: 0.12 + Math.random() * 0.1,
        sp: 0.4 + Math.random() * 0.6,
      })),
    []
  );

  useFrame((state, delta) => {
    const p = scrollRef.current;
    spin.current += delta * 0.28;
    if (group.current) {
      group.current.rotation.y = spin.current + p * 1.4;
      group.current.rotation.x = 0.32 + p * 0.55;
      group.current.position.y = -p * 1.4;
      const sc = 1 - p * 0.18;
      group.current.scale.setScalar(sc);
    }
    if (shards.current) {
      const t = state.clock.elapsedTime;
      shards.current.children.forEach((m, i) => {
        m.rotation.x += delta * shardCfg[i].sp * 0.5;
        m.rotation.z += delta * shardCfg[i].sp * 0.3;
        m.position.y = shardCfg[i].pos[1] + Math.sin(t * shardCfg[i].sp + i) * 0.04;
      });
    }
  });

  return (
    <group ref={group} rotation={[0.32, 0, 0]}>
      {/* Cylindrical shell */}
      <mesh rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[1, 1, 2.5, 44, 6, true]} />
        <meshBasicMaterial color={CYAN} wireframe transparent opacity={0.32} />
      </mesh>

      {/* Elliptical heads */}
      <mesh position={[1.25, 0, 0]} rotation={[0, 0, -Math.PI / 2]} scale={[0.55, 1, 1]}>
        <sphereGeometry args={[1, 40, 22, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshBasicMaterial color={CYAN} wireframe transparent opacity={0.3} />
      </mesh>
      <mesh position={[-1.25, 0, 0]} rotation={[0, 0, Math.PI / 2]} scale={[0.55, 1, 1]}>
        <sphereGeometry args={[1, 40, 22, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshBasicMaterial color={CYAN} wireframe transparent opacity={0.3} />
      </mesh>

      {/* Relief nozzle (PSV-402) on top */}
      <mesh position={[0, 1.12, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 0.34, 16, 1, true]} />
        <meshBasicMaterial color={CYAN} wireframe transparent opacity={0.5} />
      </mesh>
      <mesh position={[0, 1.3, 0]}>
        <cylinderGeometry args={[0.2, 0.2, 0.06, 16]} />
        <meshBasicMaterial color={CYAN} wireframe transparent opacity={0.5} />
      </mesh>

      {/* Saddle supports */}
      {[-0.7, 0.7].map((x) => (
        <mesh key={x} position={[x, -1.05, 0]}>
          <boxGeometry args={[0.16, 0.5, 1.4]} />
          <meshBasicMaterial color={CYAN} wireframe transparent opacity={0.18} />
        </mesh>
      ))}

      {/* Rupture seam */}
      <Line points={lipA} color={RED} lineWidth={1} transparent opacity={0.55} />
      <Line points={lipB} color={RED} lineWidth={1} transparent opacity={0.55} />
      <Line points={center} color={RED} lineWidth={2.5} />
      <Line points={center} color={"#d98a7e"} lineWidth={6} transparent opacity={0.15} />

      {/* Torn shell fragments near rupture */}
      <group ref={shards}>
        {shardCfg.map((c, i) => (
          <mesh key={i} position={c.pos} rotation={c.rot}>
            <tetrahedronGeometry args={[c.s, 0]} />
            <meshBasicMaterial color={i % 2 ? RED : CYAN} wireframe transparent opacity={0.6} />
          </mesh>
        ))}
      </group>

      {/* HUD orbit ring */}
      <Ring />
    </group>
  );
}

function Ring() {
  const ring = useRef();
  const pts = useMemo(() => {
    const arr = [];
    const n = 96;
    for (let i = 0; i <= n; i++) {
      const a = (i / n) * Math.PI * 2;
      arr.push([Math.cos(a) * 1.9, 0, Math.sin(a) * 1.9]);
    }
    return arr;
  }, []);
  useFrame((_, delta) => {
    if (ring.current) ring.current.rotation.y -= delta * 0.12;
  });
  return (
    <group ref={ring} rotation={[Math.PI / 2.6, 0, 0]}>
      <Line points={pts} color={CYAN} lineWidth={1} transparent opacity={0.12} />
    </group>
  );
}

export default function Hero3D() {
  const scrollRef = useHeroScroll();
  return (
    <Canvas
      dpr={[1, 1.6]}
      camera={{ position: [0.4, 0.25, 5.4], fov: 42 }}
      gl={{ antialias: true, alpha: true }}
      style={{ pointerEvents: "none" }}
    >
      <Vessel scrollRef={scrollRef} />
    </Canvas>
  );
}
